/**
 * Demo Development Tools
 * 
 * Helper tools for demo development - scene thread generation, session management, etc.
 */

import React, { useState } from 'react';
import { DemoAPI } from '../../services/demo-api';
import './DemoDevTools.css';

interface DemoDevToolsProps {
  sessionKey?: string;
  onRefresh?: () => void;
}

export const DemoDevTools: React.FC<DemoDevToolsProps> = ({
  sessionKey,
  onRefresh
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateAllScenes = async () => {
    if (!sessionKey) {
      setError('No session key available');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      const response = await DemoAPI.generateAllSceneThreads(sessionKey);
      console.log('✅ Generated all scene threads:', response);
      setResult(`✅ Created ${response.created_count}/${response.total_scenes} scene threads`);
      
      // Refresh the UI after a delay
      setTimeout(() => {
        onRefresh?.();
      }, 1000);
    } catch (err) {
      console.error('❌ Failed to generate scene threads:', err);
      setError(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <div 
        className={`demo-dev-tools-fab ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Demo Dev Tools"
      >
        ⚙️
      </div>

      {/* Dev Tools Panel */}
      {isOpen && (
        <div className="demo-dev-tools-panel">
          <div className="panel-header">
            <h3>🛠️ Demo Dev Tools</h3>
            <button 
              className="close-button"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>

          <div className="panel-content">
            <div className="tool-section">
              <h4>Scene Management</h4>
              <p className="tool-description">
                Generate thread cards for all 10 demo scenes at once
              </p>
              
              <button
                className="tool-button primary"
                onClick={handleGenerateAllScenes}
                disabled={isGenerating || !sessionKey}
              >
                {isGenerating ? '⏳ Generating...' : '🚀 Generate All Scene Threads'}
              </button>

              {result && (
                <div className="tool-result success">
                  {result}
                </div>
              )}

              {error && (
                <div className="tool-result error">
                  {error}
                </div>
              )}

              {!sessionKey && (
                <div className="tool-result warning">
                  ⚠️ No active demo session
                </div>
              )}
            </div>

            <div className="tool-section">
              <h4>Session Info</h4>
              <div className="info-row">
                <span className="info-label">Session Key:</span>
                <span className="info-value">
                  {sessionKey ? sessionKey.substring(0, 12) + '...' : 'None'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DemoDevTools;

